'use client';

import { createContext, ReactNode, useContext } from 'react';

interface DictionaryItem {
  [key: string]: DictionaryItem | string;
}

type I18nDictionary = Record<string, DictionaryItem>;

type I18nVariables = Record<string, string | number | undefined>;

interface I18nContextValue {
  locale: string;
  t: (key: string, variables?: I18nVariables, fallback?: string) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

interface I18nProviderProps {
  children: ReactNode;
  dictionary: I18nDictionary;
  locale?: string;
}

export const I18nProvider = ({
  children,
  dictionary,
  locale = 'da',
}: I18nProviderProps) => {
  const t = (
    key: string,
    variables?: I18nVariables,
    fallback?: string,
  ): string => {
    const keys = key.split('.');
    let value: string | DictionaryItem | undefined = dictionary;

    for (const k of keys) {
      if (typeof value === 'object' && value !== null) {
        value = value[k];
      } else {
        value = undefined;
        break;
      }
    }

    let result = typeof value === 'string' ? value : fallback || key;

    // Replace variables in the format {{variableName}}
    if (variables) {
      Object.entries(variables).forEach(([varKey, varValue]) => {
        result = result.replace(
          new RegExp(`{{\\s*${varKey}\\s*}}`, 'g'),
          String(varValue),
        );
      });
    }

    return result;
  };

  return (
    <I18nContext.Provider value={{ locale, t }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => {
  const context = useContext(I18nContext);

  if (!context) {
    // Return a fallback implementation if context is not available
    return {
      locale: 'da',
      t: (key: string, variables?: I18nVariables, fallback?: string) => {
        let result = fallback || key;

        if (variables) {
          Object.entries(variables).forEach(([varKey, varValue]) => {
            result = result.replace(
              new RegExp(`{{\\s*${varKey}\\s*}}`, 'g'),
              String(varValue),
            );
          });
        }

        return result;
      },
    };
  }

  return context;
};
